import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Container, 
  CircularProgress, 
  Box, 
  Tabs, 
  Tab,
  Grid,
  Paper,
  Hidden,
  FormControlLabel,
  Switch,
  withStyles
} from '@material-ui/core';
import { TableChart, InsertChart } from '@material-ui/icons';
import { withAuthorization } from '../Session';
import { isScheduler } from '../../constants';
import StatisticsFilter from './StatisticsFilter';
import CollapsibleSection from './CollapsibleSection';
import StatsTableView from './StatsTableView';
import StatsChartView from './StatsChartView';
import { STATS_PRESETS, getStatsDateRange } from '../../utils/dateFilters';
import { fetchStats } from '../../services/statsService';
import { withFirebase } from '../Firebase';
import { compose } from 'recompose';
import { appStyles } from '../../styles';

const SummaryRow = ({ stats, showDetailedCnx, classes }) => {
  if (!stats) return null;

  const getPct = (val) => stats.total > 0 ? ((val / stats.total) * 100).toFixed(1) : '0.0';

  const ValueWithPct = ({ val }) => (
    <Box textAlign="center">
      <Typography className={classes.statsSummaryValue}>{val}</Typography>
      <Typography className={classes.statsPctText}>({getPct(val)}%)</Typography>
    </Box>
  );

  return (
    <Grid container alignItems="center">
      {showDetailedCnx ? (
        <>
          <Grid item xs={1} align="center">
            <Typography className={classes.statsSummaryValue}>{stats.total}</Typography>
          </Grid>
          <Grid item xs={2} align="center"><ValueWithPct val={stats.completed} /></Grid>
          <Grid item xs={1} align="center"><ValueWithPct val={stats.unknown} /></Grid>
          <Grid item xs={2} align="center"><ValueWithPct val={stats.byStatus['USER_CNX'] || 0} /></Grid>
          <Grid item xs={2} align="center"><ValueWithPct val={stats.byStatus['MX_CNX'] || 0} /></Grid>
          <Grid item xs={2} align="center"><ValueWithPct val={stats.byStatus['WX_CNX'] || 0} /></Grid>
          <Grid item xs={2} align="center"><ValueWithPct val={stats.byStatus['OPS_CNX'] || 0} /></Grid>
        </>
      ) : (
        <>
          <Grid item xs={2} align="center">
            <Typography className={classes.statsSummaryValue}>{stats.total}</Typography>
          </Grid>
          <Grid item xs={3} align="center"><ValueWithPct val={stats.completed} /></Grid>
          <Grid item xs={3} align="center"><ValueWithPct val={stats.unknown} /></Grid>
          <Grid item xs={4} align="center"><ValueWithPct val={stats.cancelled} /></Grid>
        </>
      )}
    </Grid>
  );
};

const HoursSummaryRow = ({ hours, classes }) => {
  if (!hours) return null;
  const base = hours.scheduled;
  const getPct = (val) => base > 0 ? ((val / base) * 100).toFixed(1) : '0.0';

  const ValueWithPct = ({ val }) => (
    <Box textAlign="center">
      <Typography className={classes.statsSummaryValue}>{val.toFixed(1)}</Typography>
      <Typography className={classes.statsPctText}>({getPct(val)}%)</Typography>
    </Box>
  );

  return (
    <Grid container alignItems="center">
      <Grid item xs={2} align="center">
        <Typography className={classes.statsSummaryValue}>{hours.scheduled.toFixed(1)}</Typography>
      </Grid>
      <Grid item xs={2} align="center"><ValueWithPct val={hours.flown} /></Grid>
      <Grid item xs={2} align="center"><ValueWithPct val={hours.lostCancelled} /></Grid>
      <Grid item xs={2} align="center"><ValueWithPct val={hours.lostUnderfly} /></Grid>
      <Grid item xs={2} align="center"><ValueWithPct val={hours.gainedOverfly} /></Grid>
      <Grid item xs={2} align="center"><ValueWithPct val={hours.upcoming} /></Grid>
    </Grid>
  );
};

const StatisticsPage = (props) => {
  const { classes } = props;
  const [filter, setFilter] = useState({
    preset: STATS_PRESETS.LAST_30_DAYS,
    ...getStatsDateRange(STATS_PRESETS.LAST_30_DAYS)
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetailedCnx, setShowDetailedCnx] = useState(false);
  
  const [flightView, setFlightView] = useState(0); // 0: Table, 1: Chart
  const [eventView, setEventView] = useState(0);
  const [hoursView, setHoursView] = useState(0);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const aggregatedStats = await fetchStats(props.firebase, filter.startDate, filter.endDate);
        setStats(aggregatedStats);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [filter, props.firebase]);

  return (
    <Container className={classes.statisticsContainer}>
      <StatisticsFilter filter={filter} onChange={handleFilterChange} />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={showDetailedCnx}
              onChange={(e) => setShowDetailedCnx(e.target.checked)}
              color="primary"
            />
          }
          label="Detailed Cancellations"
        />
      </Box>
      
      {loading && !stats ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {loading && <Box mb={2} textAlign="center"><CircularProgress size={24} /></Box>}
          
          <Hidden xsDown>
            <Paper className={classes.statsHeaderRow}>
              <Grid container alignItems="center">
                <Grid item xs={3} sm={2}>
                  <Typography className={classes.statsHeaderText} style={{ textAlign: 'left' }}>Type</Typography>
                </Grid>
                <Grid item xs={8} sm={9}>
                  <Grid container>
                    {showDetailedCnx ? (
                      <>
                        <Grid item xs={1} align="center"><Typography className={classes.statsHeaderText}>Total</Typography></Grid>
                        <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Comp</Typography></Grid>
                        <Grid item xs={1} align="center"><Typography className={classes.statsHeaderText}>Unk</Typography></Grid>
                        <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>User</Typography></Grid>
                        <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Mx</Typography></Grid>
                        <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Wx</Typography></Grid>
                        <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Ops</Typography></Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Total</Typography></Grid>
                        <Grid item xs={3} align="center"><Typography className={classes.statsHeaderText}>Completed</Typography></Grid>
                        <Grid item xs={3} align="center"><Typography className={classes.statsHeaderText}>Unknown</Typography></Grid>
                        <Grid item xs={4} align="center"><Typography className={classes.statsHeaderText}>Cancelled</Typography></Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={1} />
              </Grid>
            </Paper>
          </Hidden>

          <CollapsibleSection 
            title="Flights" 
            summary={<SummaryRow stats={stats?.flights} showDetailedCnx={showDetailedCnx} classes={classes} />}
          >
            <Box mb={2}>
              <Tabs
                value={flightView}
                onChange={(e, v) => setFlightView(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab icon={<TableChart />} label="Table" />
                <Tab icon={<InsertChart />} label="Chart" />
              </Tabs>
            </Box>
            {flightView === 0 ? (
              <StatsTableView data={stats?.flights.byType} showDetailedCnx={showDetailedCnx} />
            ) : (
              <StatsChartView data={stats?.flights.byType} showDetailedCnx={showDetailedCnx} />
            )}
          </CollapsibleSection>

          <CollapsibleSection 
            title="Events" 
            summary={<SummaryRow stats={stats?.events} showDetailedCnx={showDetailedCnx} classes={classes} />}
          >
            <Box mb={2}>
              <Tabs
                value={eventView}
                onChange={(e, v) => setEventView(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab icon={<TableChart />} label="Table" />
                <Tab icon={<InsertChart />} label="Chart" />
              </Tabs>
            </Box>
            {eventView === 0 ? (
              <StatsTableView data={stats?.events.byType} showDetailedCnx={showDetailedCnx} />
            ) : (
              <StatsChartView data={stats?.events.byType} showDetailedCnx={showDetailedCnx} />
            )}
          </CollapsibleSection>

          <Hidden xsDown>
            <Paper className={classes.statsHeaderRow}>
              <Grid container alignItems="center">
                <Grid item xs={3} sm={2}>
                  <Typography className={classes.statsHeaderText} style={{ textAlign: 'left' }}>Type</Typography>
                </Grid>
                <Grid item xs={8} sm={9}>
                  <Grid container>
                    <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Scheduled</Typography></Grid>
                    <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Flown</Typography></Grid>
                    <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Lost (Cnx)</Typography></Grid>
                    <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Lost (Under)</Typography></Grid>
                    <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Gained</Typography></Grid>
                    <Grid item xs={2} align="center"><Typography className={classes.statsHeaderText}>Upcoming</Typography></Grid>
                  </Grid>
                </Grid>
                <Grid item xs={1} />
              </Grid>
            </Paper>
          </Hidden>

          <CollapsibleSection 
            title="Hours" 
            summary={<HoursSummaryRow hours={stats?.flights.hours} classes={classes} />}
          >
            <Box mb={2}>
              <Tabs
                value={hoursView}
                onChange={(e, v) => setHoursView(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab icon={<TableChart />} label="Table" />
                <Tab icon={<InsertChart />} label="Chart" />
              </Tabs>
            </Box>
            {hoursView === 0 ? (
              <StatsTableView data={stats?.flights.byType} isHours={true} />
            ) : (
              <StatsChartView data={stats?.flights.byType} isHours={true} />
            )}
          </CollapsibleSection>
        </>
      )}
    </Container>
  );
};

export default compose(
  withStyles(appStyles),
  withAuthorization(isScheduler),
  withFirebase
)(StatisticsPage);
